import React, {useEffect} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {Button, Checkbox} from "@spg/component_library";
import {
  FileUploadDataset,
  DisplayableDataset,
  DownloadableDataset,
} from "../../../logic/APITypes";
import {formatStructuredText} from "../../../logic/structuredText/Formatting";
import Prism from "../../../logic/structuredText/prism.js";
import "../../../logic/structuredText/prism.css";
import {TopicContentViewerDownloadBlob} from "./TopicContentViewerDownloadBlob";
import {CopyToClipboard} from "../../../reusables/CopyToClipboard";

/**
 * A text block with key-value pairs.
 */
function TopicContentViewerContent(props: {
  currentDataset:
    | ({type: "file"} & FileUploadDataset)
    | ({type: "display"} & DisplayableDataset)
    | ({type: "download"} & DownloadableDataset);
  shouldFormat: {get: boolean; set: (value: boolean) => void};
  selection?: JSX.Element;
  topicName: string;
  reloadButtonProps: React.ComponentPropsWithoutRef<typeof Button>;
}) {
  const intl = useIntl();

  const codeLanguage =
    "language-" +
    (props.currentDataset.type === "download"
      ? "plain"
      : props.currentDataset.type === "file"
        ? "json"
        : props.currentDataset.mime_type.split("/")[1]);

  useEffect(() => {
    setTimeout(() => Prism.highlightAll(), 0);
  }, [props.currentDataset, props.shouldFormat.get]);

  if (props.currentDataset.type === "download")
    return (
      <div className="topicContentViewerGeneric">
        <FormattedMessage id={"topicContentViewer.downloadableDataset"} />
        <div className="topicContentViewerGenericButtons">
          <TopicContentViewerDownloadBlob
            currentDataset={props.currentDataset}
            topicName={props.topicName}
            content={props.currentDataset.content}
          />
          <Button {...props.reloadButtonProps} configuration="tertiary" />
        </div>
        {props.selection}
      </div>
    );

  const contentText = props.shouldFormat.get
    ? formatStructuredText(
        props.currentDataset.content,
        props.currentDataset.type === "file"
          ? "application/json"
          : props.currentDataset.mime_type,
      ) || props.currentDataset.content
    : props.currentDataset.content;

  return (
    <>
      <pre className={"line-numbers"}>
        <code className={codeLanguage}>{contentText}</code>
      </pre>
      <div className="topicContentViewerCopy">
        <CopyToClipboard
          idBase={"topicContentViewerCopy"}
          text={contentText}
          title={intl.formatMessage({
            id:
              "topicContentViewer.copy" +
              (props.shouldFormat.get ? ".formatted" : ""),
          })}
        />
      </div>
      {props.selection}
      <div className="topicContentViewerControls">
        {props.currentDataset?.type === "file" ? (
          <a
            id="topicContentViewerDownloadLink"
            className="topicContentViewerDownload styleLinkAsButton tertiary wide"
            target="_blank"
            rel="noreferrer"
            href={props.currentDataset.download_url}
            download={(() => {
              try {
                return JSON.parse(props.currentDataset.content)[0][
                  "attachment"
                ]["original_filename"];
              } catch (e) {
                return (
                  `${props.topicName}-${intl.formatDate(
                    props.currentDataset.received_at,
                    {timeStyle: "medium", dateStyle: "short"},
                  )}`.replace(/[/\\:*?"<>.]/g, "_") +
                  props.currentDataset.mime_type
                );
              }
            })()}
          >
            <FormattedMessage id="topicContentViewer.controls.download" />
          </a>
        ) : (
          <>
            <TopicContentViewerDownloadBlob
              currentDataset={props.currentDataset}
              topicName={props.topicName}
              content={contentText}
            />
            <Checkbox
              label={intl.formatMessage({
                id: "topicContentViewer.controls.format",
              })}
              idBase={"topicContentViewerFormat"}
              setValue={props.shouldFormat.set}
              isChecked={props.shouldFormat.get}
            />
          </>
        )}

        <Button {...props.reloadButtonProps} />
      </div>
    </>
  );
}

export {TopicContentViewerContent};
